/**
 * useAutoTrade
 * Subscribes to incoming UT Bot signals and automatically places paper trades.
 * BUY signal  → open LONG (close any open SHORT first)
 * SELL signal → close any open LONG (open SHORT if mode = 'both' | 'short_only')
 */
import { useEffect, useRef } from 'react';
import { useTerminalStore } from '../stores/terminal.store';
import { generateId } from '@arc/shared';
import type { Order, Position, AutoTradeRecord, UTBotSignal } from '@arc/shared';

export function useAutoTrade() {
  const signalCountRef = useRef(0);

  useEffect(() => {
    // Initialise counter so we don't replay all historical signals on mount
    const initial = useTerminalStore.getState().signals;
    signalCountRef.current = Object.values(initial).flat().length;

    const unsub = useTerminalStore.subscribe(
      (state) => state.signals,
      (signals) => {
        const state = useTerminalStore.getState();
        if (!state.autoTradeConfig.enabled) return;

        const all = Object.values(signals)
          .flat()
          .sort((a, b) => a.timestamp - b.timestamp); // oldest first

        const newCount = all.length;
        if (newCount <= signalCountRef.current) return;

        const incoming = all.slice(signalCountRef.current);
        signalCountRef.current = newCount;

        for (const signal of incoming) {
          processSignal(signal);
        }
      }
    );

    return () => unsub();
  }, []);
}

// ─── Signal processor ─────────────────────────────────────────────────────────

function processSignal(signal: UTBotSignal) {
  const state = useTerminalStore.getState();
  const { autoTradeConfig, positions, autoTradeRecords,
          placeOrder, openPosition, closePosition,
          addAutoTradeRecord, closeAutoTradeRecord } = state;

  if (!autoTradeConfig.enabled) return;

  // Symbol filter
  if (autoTradeConfig.symbols.length > 0 && !autoTradeConfig.symbols.includes(signal.symbol)) return;

  // Timeframe filter
  if (autoTradeConfig.timeframes.length > 0 && !autoTradeConfig.timeframes.includes(signal.timeframe)) return;

  const { symbol, price: fillPrice, timeframe } = signal;

  // Find existing paper position and open auto-trade record for this symbol
  const openPos    = positions.find(p => p.symbol === symbol && p.mode === 'paper' && p.exchange === 'paper');
  const openRecord = autoTradeRecords.find(r => r.symbol === symbol && r.status === 'open');

  if (signal.type === 'BUY') {
    // --- Close any open SHORT ------------------------------------------------
    if (openPos && openPos.side === 'sell' && openRecord) {
      closePosition(openPos.id, fillPrice);
      closeAutoTradeRecord(openRecord.id, fillPrice, signal.id);
    }

    // --- Open LONG if allowed -------------------------------------------------
    if (autoTradeConfig.mode === 'long_only' || autoTradeConfig.mode === 'both') {
      // Don't stack same-side positions for the same symbol
      if (openPos?.side === 'buy') return;

      const currentPaperPositions = useTerminalStore.getState().positions.filter(
        p => p.mode === 'paper' && p.exchange === 'paper'
      );
      if (currentPaperPositions.length >= autoTradeConfig.maxPositions) return;

      const orderId    = generateId();
      const positionId = generateId();
      const recordId   = generateId();

      const order: Order = {
        id:        orderId,
        symbol,
        side:      'buy',
        type:      'market',
        size:      autoTradeConfig.sizeUsd,
        leverage:  autoTradeConfig.leverage,
        fillPrice,
        status:    'filled',
        mode:      'paper',
        exchange:  'paper',
        timestamp: signal.timestamp,
        closedAt:  signal.timestamp,
      };

      const position: Position = {
        id:               positionId,
        orderId,
        symbol,
        side:             'buy',
        size:             autoTradeConfig.sizeUsd,
        entryPrice:       fillPrice,
        currentPrice:     fillPrice,
        unrealizedPnl:    0,
        unrealizedPnlPct: 0,
        leverage:         autoTradeConfig.leverage,
        mode:             'paper',
        exchange:         'paper',
        openedAt:         signal.timestamp,
      };

      const record: AutoTradeRecord = {
        id:             recordId,
        symbol,
        side:           'buy',
        entryPrice:     fillPrice,
        sizeUsd:        autoTradeConfig.sizeUsd,
        openedAt:       signal.timestamp,
        status:         'open',
        timeframe,
        entrySignalId:  signal.id,
      };

      placeOrder(order);
      openPosition(position);
      addAutoTradeRecord(record);
    }
  }

  if (signal.type === 'SELL') {
    // --- Close any open LONG -------------------------------------------------
    if (openPos && openPos.side === 'buy' && openRecord) {
      closePosition(openPos.id, fillPrice);
      closeAutoTradeRecord(openRecord.id, fillPrice, signal.id);
    }

    // --- Open SHORT if allowed ------------------------------------------------
    if (autoTradeConfig.mode === 'short_only' || autoTradeConfig.mode === 'both') {
      if (openPos?.side === 'sell') return;

      const currentPaperPositions = useTerminalStore.getState().positions.filter(
        p => p.mode === 'paper' && p.exchange === 'paper'
      );
      if (currentPaperPositions.length >= autoTradeConfig.maxPositions) return;

      const orderId    = generateId();
      const positionId = generateId();
      const recordId   = generateId();

      const order: Order = {
        id:        orderId,
        symbol,
        side:      'sell',
        type:      'market',
        size:      autoTradeConfig.sizeUsd,
        leverage:  autoTradeConfig.leverage,
        fillPrice,
        status:    'filled',
        mode:      'paper',
        exchange:  'paper',
        timestamp: signal.timestamp,
        closedAt:  signal.timestamp,
      };

      const position: Position = {
        id:               positionId,
        orderId,
        symbol,
        side:             'sell',
        size:             autoTradeConfig.sizeUsd,
        entryPrice:       fillPrice,
        currentPrice:     fillPrice,
        unrealizedPnl:    0,
        unrealizedPnlPct: 0,
        leverage:         autoTradeConfig.leverage,
        mode:             'paper',
        exchange:         'paper',
        openedAt:         signal.timestamp,
      };

      const record: AutoTradeRecord = {
        id:            recordId,
        symbol,
        side:          'sell',
        entryPrice:    fillPrice,
        sizeUsd:       autoTradeConfig.sizeUsd,
        openedAt:      signal.timestamp,
        status:        'open',
        timeframe,
        entrySignalId: signal.id,
      };

      placeOrder(order);
      openPosition(position);
      addAutoTradeRecord(record);
    }
  }
}
